import { useState, useMemo } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Plus, FolderOpen, CheckCircle2, Clock, Loader2 } from "lucide-react";
import { AppContainer } from "@/components/layout/app-container";
import Pagination from "@/components/dashboard/pagination";
import { TimeFilterDropdown } from "@/pages/contrato/components/time-filter-dropdown";
import { ProjectStatsCard } from "@/components/projetos/project-stats-card";
import { ProjectTable } from "@/components/projetos/project-table";
import ProjectFilterDialog, { IProjectFilters } from "@/components/projetos/project-filter-dialog";
import { useGetProjects, useDeleteProject } from "@/services/queries/useProject";
import { transformMovieToTableData } from "@/lib/transform-movie";
import { useDebounce } from "@/hooks/use-debounce";
import InputSearch from "@/components/ui/input-search";
import ButtonFilter from "@/components/ui/button-filter";

// Mock chart data for sparklines
const mockChartData = [
  { value: 15 },
  { value: 18 },
  { value: 12 },
  { value: 20 },
  { value: 16 },
  { value: 22 },
  { value: 19 },
  { value: 25 },
  { value: 23 },
  { value: 28 },
];

export default function Projetos() {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<IProjectFilters>({
    status: [],
    dateFrom: "",
    dateTo: "",
    segment: [],
  });

  // Fetch projects from API
  const { data: apiResponse, isLoading, error } = useGetProjects();

  // Delete mutation
  const deleteMutation = useDeleteProject();

  // Transform API response to table format
  const allProjects = useMemo(() => {
    if (!apiResponse) return [];
    return apiResponse?.map(transformMovieToTableData);
  }, [apiResponse]);

  const itemsPerPage = 10;

  // Filter and search projects
  const filteredProjects = useMemo(() => {
    return allProjects.filter((project) => {
      const matchesSearch =
        project.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        project.salicNumber.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        project.proponent.toLowerCase().includes(debouncedSearchTerm.toLowerCase());

      const matchesStatus =
        filters.status.length === 0 || filters.status.includes(project.status);

      return matchesSearch && matchesStatus;
    });
  }, [allProjects, debouncedSearchTerm, filters]);

  // Pagination
  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProjects = filteredProjects.slice(startIndex, startIndex + itemsPerPage);

  // Stats calculations
  const stats = {
    total: allProjects.length,
    active: allProjects.filter(p => p.status === "Ativo").length,
    completed: allProjects.filter(p => p.status === "Concluído").length,
    development: allProjects.filter(p => p.status === "Em Desenvolvimento").length,
  };

  const handleApplyFilters = (newFilters: IProjectFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
    setIsFilterOpen(false);
  };

  const handleDelete = (id: number) => {
    // Find project name for confirmation
    const project = allProjects.find(p => p.id === id);
    if (!project) return;

    // Confirm deletion
    if (confirm(`Tem certeza que deseja excluir o projeto "${project.name}"?`)) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <AppContainer
      title="Projetos"
      description="Projeto iniciados em desenvolvimento e concluídos"
      button={
        <Link href="/cadastrar-projeto">
          <Button className="gap-2">
            <Plus className="w-4 h-4" />
            Criar Novo Projeto
          </Button>
        </Link>
      }
    >
      <div className="space-y-6">
        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-2 text-gray-400">Carregando projetos...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-500/10 border border-red-500 rounded-xl p-6">
            <p className="text-red-500">
              Erro ao carregar projetos. Por favor, tente novamente.
            </p>
          </div>
        )}

        {/* Content - only show if not loading and no error */}
        {!isLoading && !error && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <ProjectStatsCard
                icon={FolderOpen}
                title="Total de Projetos"
                value={stats.total}
                changeText="Últimos 30 dias"
                percentage="+12%"
                chartColor="#7846FF"
                chartData={mockChartData}
              />
              <ProjectStatsCard
                icon={CheckCircle2}
                title="Projetos Ativos"
                value={stats.active}
                changeText="Últimos 30 dias"
                percentage="+8%"
                chartColor="#13D480"
                chartData={mockChartData}
              />
              <ProjectStatsCard
                icon={CheckCircle2}
                title="Projetos Concluídos"
                value={stats.completed}
                changeText="Últimos 30 dias"
                percentage="+15%"
                chartColor="#2078D7"
                chartData={mockChartData}
              />
              <ProjectStatsCard
                icon={Clock}
                title="Em Desenvolvimento"
                value={stats.development}
                changeText="Últimos 30 dias"
                percentage="+5%"
                chartColor="#F2C71E"
                chartData={mockChartData}
              />
            </div>

            {/* Filters and Search */}
            <div className="bg-darkblue rounded-xl p-6">
              <div className="flex flex-col md:flex-row gap-4 items-center justify-between mb-6">
                <InputSearch
                  value={searchTerm}
                  positionIcon="start"
                  onChange={setSearchTerm}
                  placeholder="nome, número salic ou proponente..."
                  className="ml-4 text-sm"
                  classNameIcon="text-gray-400"
                  classNameContainer="w-80"
                />

                <div className="flex items-center gap-4">
                  <ButtonFilter
                    positionIcon="start"
                    onClick={setIsFilterOpen as () => void}
                  />

                  <TimeFilterDropdown />
                </div>
              </div>

              {/* Table */}
              <ProjectTable projects={paginatedProjects} onDelete={handleDelete} />

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6 flex justify-center">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </div>
          </>
        )}

        {/* Filter Dialog */}
        <ProjectFilterDialog
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          filters={filters}
          onApply={handleApplyFilters}
        />
      </div>
    </AppContainer>
  );
}
